import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import PersonalStep from "./wizard/PersonalStep";
import PropertyStep from "./wizard/PropertyStep";
import PreferencesStep from "./wizard/PreferencesStep";
import ResultsStep from "./wizard/ResultsStep";
import { ArrowLeft, Calculator, CheckCircle } from "lucide-react";
import { useWizardTracking } from "@/hooks/useWizardTracking";
import { getRecommendations } from "@/utils/recommendations";
import { Json } from "@/integrations/supabase/types";

export interface FormData {
  // Schritt 1: Persönliche Situation
  age?: string;
  familyStatus?: string;
  financialSituation?: string;
  mainReason?: string;
  
  // Schritt 2: Immobilie
  propertyType?: string;
  estimatedValue?: string;
  location?: string;
  condition?: string;
  livingDuration?: string;
  
  // Schritt 3: Präferenzen
  paymentForm?: string;
  priority?: string;
  monthlyPayments?: string;
  futurePlans?: string;
}

const AdvisorWizard = () => {
  const [currentStep, setCurrentStep] = useState<'intro' | 'personal' | 'property' | 'preferences' | 'results'>('intro');
  const [formData, setFormData] = useState<FormData>({});
  const { initSession, trackStep, trackCompletion, resetTracking } = useWizardTracking();

  // Initialize tracking when wizard starts
  useEffect(() => {
    if (currentStep === 'personal') {
      initSession();
    }
  }, [currentStep, initSession]);

  const steps = [
    { id: 'personal', title: 'Persönliche Situation', description: '5 Fragen zu Ihnen' },
    { id: 'property', title: 'Ihre Immobilie', description: '5 Fragen zur Immobilie' },
    { id: 'preferences', title: 'Ihre Wünsche', description: '4 Fragen zu Präferenzen' }
  ];

  const getProgress = () => {
    switch (currentStep) {
      case 'intro': return 0;
      case 'personal': return 33;
      case 'property': return 66;
      case 'preferences': return 100;
      case 'results': return 100;
      default: return 0;
    }
  };

  const getCurrentStepIndex = () => {
    switch (currentStep) {
      case 'personal': return 0;
      case 'property': return 1;
      case 'preferences': return 2;
      default: return -1;
    }
  };

  const handleStepComplete = async (stepData: Partial<FormData>, nextStep: string) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);
    
    // Speichere in Session Storage
    sessionStorage.setItem('advisorFormData', JSON.stringify(updatedData));
    
    // Track step with form data
    await trackStep(nextStep, updatedData);
    
    // If completing to results, also track the completion with recommendations
    if (nextStep === 'results') {
      const recommendations = getRecommendations(updatedData as FormData);
      await trackCompletion(updatedData as FormData, recommendations as unknown as Json);
    }
    
    setCurrentStep(nextStep as any);
    
    // Scroll zum Schritt-Bereich statt ganz nach oben
    setTimeout(() => {
      document.getElementById('advisor-wizard')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'personal':
        setCurrentStep('intro');
        break;
      case 'property':
        setCurrentStep('personal');
        break;
      case 'preferences':
        setCurrentStep('property');
        break;
      case 'results':
        setCurrentStep('preferences');
        break;
    }
    setTimeout(() => {
      document.getElementById('advisor-wizard')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const handleStartOver = () => {
    setFormData({});
    sessionStorage.removeItem('advisorFormData');
    resetTracking();
    setCurrentStep('intro');
    setTimeout(() => {
      document.getElementById('advisor-wizard')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  if (currentStep === 'intro') {
    return (
      <section className="py-16 md:py-24 bg-background/70" data-advisor-wizard>
        <div className="container-comfortable">
          <Card className="max-w-4xl mx-auto card-elevated">
            <CardHeader className="text-center pb-8">
              <div className="bg-accent/10 p-6 rounded-xl w-fit mx-auto mb-6">
                <Calculator className="h-12 w-12 text-accent" />
              </div>
              <CardTitle className="text-3xl md:text-4xl text-primary mb-4">
                Ihr persönlicher Verrentungsberater
              </CardTitle>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto text-balance">
                In nur 3 Schritten finden wir die optimale Immobilienverrentung für Ihre Situation. 
                Völlig kostenlos und unverbindlich.
              </p>
            </CardHeader>
            
            <CardContent className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {steps.map((step, index) => (
                  <div key={step.id} className="text-center p-6 bg-card border border-border rounded-xl">
                    <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                      {index + 1}
                    </div>
                    <h3 className="font-semibold text-lg text-primary mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <Button 
                  variant="accent" 
                  size="lg" 
                  className="text-lg md:text-xl px-8 md:px-12 py-4 leading-tight"
                  onClick={() => {
                    setCurrentStep('personal');
                    setTimeout(() => document.getElementById('advisor-wizard')?.scrollIntoView({ behavior: 'smooth' }), 50);
                  }}
                >
                  <span className="text-center">
                    Jetzt starten -<br className="md:hidden" />
                    <span className="hidden md:inline"> </span>Völlig kostenlos
                  </span>
                </Button>
                <p className="text-sm text-muted-foreground mt-4">
                  ⏱️ Dauer: ca. 3-5 Minuten • 🔒 100% datenschutzkonform • ✅ Keine Registrierung nötig
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-16">
      <div className="container-comfortable">
        {/* Progress Header */}
        {currentStep !== 'results' && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </Button>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                Schritt {getCurrentStepIndex() + 1} von 3
              </div>
            </div>
            
            <div className="space-y-2">
              <Progress value={getProgress()} className="h-2" />
              <div className="flex justify-between text-sm">
                {steps.map((step, index) => (
                  <span 
                    key={step.id}
                    className={getCurrentStepIndex() >= index ? 'text-primary font-medium' : 'text-muted-foreground'}
                  >
                    {step.title}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {currentStep === 'personal' && (
            <PersonalStep 
              data={formData} 
              onComplete={(data) => handleStepComplete(data, 'property')} 
            />
          )}
          {currentStep === 'property' && (
            <PropertyStep 
              data={formData} 
              onComplete={(data) => handleStepComplete(data, 'preferences')} 
            />
          )}
          {currentStep === 'preferences' && (
            <PreferencesStep 
              data={formData} 
              onComplete={(data) => handleStepComplete(data, 'results')} 
            />
          )}
          {currentStep === 'results' && (
            <ResultsStep 
              data={formData} 
              onStartOver={handleStartOver}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default AdvisorWizard;